import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { 
  Building2, 
  Home, 
  TrendingUp, 
  DollarSign, 
  FileText, 
  Users, 
  CheckCircle2,
  Clock,
  BarChart3,
  Wallet,
  ArrowUpRight,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const OwnerDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    projects: { total: 0, active: 0 },
    units: { total: 0, sold: 0, available: 0, reserved: 0, total_m2: 0, sold_m2: 0 },
    offers: { pending: 0, accepted: 0, total_value: 0 },
    partners: { active: 0 },
    sales: { confirmed: 0, total_value: 0 },
    progress: { overall_completion: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    loadDashboard(controller.signal);
    return () => controller.abort();
  }, []);

  const loadDashboard = async (signal) => {
    try {
      const projectsRes = await axios.get("/projects", { signal });
      const projects = projectsRes.data || [];

      const unitsRes = await axios.get("/units", { signal });
      const units = unitsRes.data || [];

      const salesRes = await axios.get("/finances/sales", { signal });
      const salesData = salesRes.data || {};

      let allOffers = [];
      const partnersRes = await axios.get('/partners', { signal });
      const allPartners = partnersRes.data || [];

      for (const project of projects) {
        try {
          const offersRes = await axios.get(`/offers/${project.id}`, { signal });
          allOffers = [...allOffers, ...(offersRes.data || [])];
        } catch (err) {}
      }

      if (signal?.aborted) return;
      const newStats = {
        projects: {
          total: projects.length,
          active: projects.filter(p => p.status === 'active' || p.status === 'in_progress').length
        },
        units: {
          total: units.length,
          sold: units.filter(u => u.status === 'sold').length,
          available: units.filter(u => u.status === 'available').length,
          reserved: units.filter(u => u.status === 'reserved').length,
          total_m2: units.reduce((sum, u) => sum + (u.net_m2 || 0), 0),
          sold_m2: units.filter(u => u.status === 'sold').reduce((sum, u) => sum + (u.net_m2 || 0), 0)
        },
        offers: {
          pending: allOffers.filter(o => o.status === 'pending').length,
          accepted: allOffers.filter(o => o.status === 'accepted').length,
          total_value: allOffers.filter(o => o.status === 'accepted').reduce((sum, o) => sum + (o.amount || 0), 0)
        },
        partners: {
          active: allPartners.filter(p => p.status === 'active').length
        },
        sales: {
          confirmed: salesData.summary?.total_sales_count || 0,
          total_value: salesData.summary?.total_contract_value || 0
        },
        progress: { overall_completion: 0 }
      };

      setStats(newStats);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Dashboard load error:", error);
        toast.error(t('messages.loadFailed'));
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-500">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  // Metric Card Component
  const MetricCard = ({ icon: Icon, iconBg, iconColor, value, label, sublabel, onClick, testId, accent }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`group relative bg-white border border-slate-200 rounded-sm p-6 text-left transition-all duration-200 hover:shadow-card-hover hover:border-slate-300 ${accent ? 'border-l-4 border-l-indigo-600' : ''}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 ${iconBg} rounded-sm flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-600 transition-colors" />
      </div>
      <p className="font-mono text-3xl font-semibold text-slate-900 tracking-tight">{value}</p>
      <p className="text-xs font-medium text-slate-500 mt-1 uppercase tracking-wider">{label}</p>
      {sublabel && <p className="text-xs text-slate-400 mt-0.5">{sublabel}</p>}
    </button>
  );

  // Large Revenue Card
  const RevenueCard = ({ value, count, onClick, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group relative bg-white border border-slate-200 rounded-sm overflow-hidden text-left transition-all duration-200 hover:shadow-card-hover col-span-2"
    >
      {/* Top accent bar */}
      <div className="h-1 bg-indigo-600" />
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="w-10 h-10 bg-indigo-50 rounded-sm flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-indigo-600" />
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-sm text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
            {count} {t('dashboard.confirmedSales')}
          </span>
        </div>
        <p className="font-mono text-4xl font-bold text-slate-900 tracking-tight">{value}</p>
        <p className="text-xs font-medium text-slate-500 mt-2 uppercase tracking-wider">{t('dashboard.confirmedSalesRevenue')}</p>
      </div>
    </button>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-10">
        {/* Header */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-1">Dashboard</p>
            <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight uppercase">
              {t('dashboard.ownerDashboard')}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{t('dashboard.realTimeOverview')}</p>
          </div>
          <div className="hidden md:block">
            <p className="text-xs text-slate-400 font-mono">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Projects & Units Section */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-5 bg-indigo-600 rounded-full" />
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              {t('projects.title')} & {t('dashboard.unitsAndSales')}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Building2}
              iconBg="bg-indigo-50"
              iconColor="text-indigo-600"
              value={stats.projects.total}
              label={t('dashboard.totalProjects')}
              onClick={() => navigate('/projects')}
              testId="kpi-total-projects"
              accent
            />
            <MetricCard
              icon={CheckCircle2}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              value={stats.projects.active}
              label={t('dashboard.activeProjects')}
              onClick={() => navigate('/projects')}
              testId="kpi-active-projects"
            />
            <MetricCard
              icon={Home}
              iconBg="bg-sky-50"
              iconColor="text-sky-600"
              value={stats.units.total}
              label={t('units.title')}
              sublabel={`${stats.units.total_m2.toFixed(0)} m²`}
              onClick={() => navigate('/units')}
              testId="kpi-total-units"
            />
            <MetricCard
              icon={TrendingUp}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              value={stats.units.sold}
              label={t('dashboard.sold')}
              sublabel={`${stats.units.sold_m2.toFixed(0)} m²`}
              onClick={() => navigate('/units')}
              testId="kpi-sold-units"
            />
          </div>
        </section>

        {/* Financial Overview Section */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-5 bg-amber-500 rounded-full" />
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              {t('dashboard.financialOverview')}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={CheckCircle2}
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
              value={stats.units.available}
              label={t('dashboard.available')}
              onClick={() => navigate('/units')}
              testId="kpi-available-units"
            />
            <MetricCard
              icon={Clock}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              value={stats.units.reserved}
              label={t('dashboard.reserved')}
              onClick={() => navigate('/reservations')}
              testId="kpi-reserved-units"
            />
            <RevenueCard
              value={formatCurrency(stats.sales.total_value)}
              count={stats.sales.confirmed}
              onClick={() => navigate('/finances')}
              testId="kpi-sales-revenue"
            />
          </div>
        </section>

        {/* Procurement & Partnerships Section */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-5 bg-rose-500 rounded-full" />
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              {t('dashboard.procurementPartnerships')}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={FileText}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              value={stats.offers.pending}
              label={t('dashboard.pendingOffers')}
              onClick={() => navigate('/offers')}
              testId="kpi-pending-offers"
            />
            <MetricCard
              icon={CheckCircle2}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              value={stats.offers.accepted}
              label={t('dashboard.acceptedPartners')}
              onClick={() => navigate('/offers')}
              testId="kpi-accepted-offers"
            />
            <MetricCard
              icon={Users}
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
              value={stats.partners.active}
              label={t('dashboard.activeSubcontractors')}
              onClick={() => navigate('/partners')}
              testId="kpi-active-partners"
            />
            <MetricCard
              icon={BarChart3}
              iconBg="bg-rose-50"
              iconColor="text-rose-600"
              value="—"
              label={t('nav.projectProgress')}
              onClick={() => navigate('/project-progress')}
              testId="quick-action-progress"
            />
          </div>
        </section>

        {/* Quick Actions */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-5 bg-slate-400 rounded-full" />
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              Quick Actions
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: BarChart3, label: t('nav.projectProgress'), path: '/project-progress', color: 'text-rose-600', bg: 'bg-rose-50' },
              { icon: FileText, label: t('nav.offers'), path: '/offers', color: 'text-amber-600', bg: 'bg-amber-50' },
              { icon: Wallet, label: t('projects.budget'), path: '/budget', color: 'text-violet-600', bg: 'bg-violet-50' },
              { icon: Home, label: t('units.title'), path: '/units', color: 'text-sky-600', bg: 'bg-sky-50' },
            ].map((action) => (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                data-testid={`quick-action-${action.path.slice(1)}`}
                className="group flex items-center justify-between p-4 bg-white border border-slate-200 rounded-sm hover:border-slate-300 hover:shadow-card-hover transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 ${action.bg} rounded-sm flex items-center justify-center`}>
                    <action.icon className={`h-4 w-4 ${action.color}`} />
                  </div>
                  <span className="text-sm font-semibold text-slate-700">{action.label}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all" />
              </button>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default OwnerDashboard;
